// eslint-disable-next-line jira/import-whitelist
import {
	ATLASSIAN_CONSENT_COOKIE_KEY,
	ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
	ATLASSIAN_SHOW_BANNER_COOKIE_KEY,
	type ConsentHubResponse,
	type PrefetchedConsentPreferenceData,
} from '@atlassian/browser-storage-controls';
// eslint-disable-next-line jira/import-whitelist
import { getCookie } from '@atlassian/jira-browser-storage-controls/src/utils/get-cookie/index.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { createResource, useResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import {
	getAtlassianAccountId,
	getEnvironment,
} from '@atlassian/jira-common-util-get-tenant-context';
import {
	CONSENT_BANNER_USER_PREFERENCES,
	CONSENT_BANNER_USER_PREFERENCES_KEY,
	CONSENT_BANNER_USER_PREFERENCES_SSR,
} from './constants.tsx';

const consentBannerUserPreferencesResourceOld = createResource<PrefetchedConsentPreferenceData>({
	type: CONSENT_BANNER_USER_PREFERENCES,
	getKey: () => CONSENT_BANNER_USER_PREFERENCES_KEY,
	getData: async () => {
		const consentToken = getCookie(ATLASSIAN_CONSENT_COOKIE_KEY);
		const fallbackConsentToken = getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY);

		// If we have cached cookie data, bundle it up, with a potential fallback token
		if (consentToken) {
			return { consentToken, fallbackConsentToken };
		}

		const data = await performGetRequest<ConsentHubResponse>(
			'/gateway/api/consenthub/session/user/consents/cookies',
		);
		return data;
	},

	maxAge: 15 * 60 * 1000,
	isBrowserOnly: true,
});

export type JiraPrefetchedConsentPreferenceData = PrefetchedConsentPreferenceData & {
	shouldShowBanner?: boolean;
};
const consentBannerUserPreferencesResourceNew = createResource<JiraPrefetchedConsentPreferenceData>(
	{
		type: CONSENT_BANNER_USER_PREFERENCES_SSR,
		getKey: () => CONSENT_BANNER_USER_PREFERENCES_KEY,
		getData: async () => {
			const consentToken = getCookie(ATLASSIAN_CONSENT_COOKIE_KEY, true);
			const fallbackConsentToken = getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY, true);
			const shouldShowBannerBit = getCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY, true);
			const shouldShowBanner =
				shouldShowBannerBit !== undefined && shouldShowBannerBit !== null
					? !!Number(shouldShowBannerBit)
					: undefined;

			// If we have cached cookie data, bundle it up, with a potential fallback token
			if (consentToken) {
				return { consentToken, fallbackConsentToken, shouldShowBanner };
			}
			/**
			 * On server we need to request the api using a server to server api (atl-preferences-center-elb)
			 * otherwise the request will not be authenticated correctly.
			 *
			 * However Pillar does not support ASAP, so we need to use the client side api call in local development.
			 *
			 * See https://atlassian.slack.com/archives/C017Y3EPAQ4/p1754626460768439?thread_ts=1754601759.549879&cid=C017Y3EPAQ4
			 */
			if (__SERVER__ && process?.env?.PILLAR_LOCAL_DEVELOPMENT !== 'true') {
				const hostEnv = getEnvironment() === 'prod' ? 'prod' : 'staging';
				const userId = getAtlassianAccountId();
				if (userId) {
					const data = await performGetRequest<ConsentHubResponse>(
						`https://atl-preferences-center-elb.${hostEnv}.atl-paas.net/rest/user/${userId}/consents/cookies`,
						{
							credentials: 'include',
							headers: {
								'x-ssr-service-name': 'atl-preferences-center',
							},
						},
					);
					return data;
				}
			}

			const data = await performGetRequest<ConsentHubResponse>(
				'/gateway/api/consenthub/session/user/consents/cookies',
			);
			return data;
		},

		maxAge: 15 * 60 * 1000,
		isBrowserOnly: false,
	},
);

export const getConsentBannerUserPreferencesResource = () => {
	if (fg('jfp-magma-ssr-iv-cookie-consent-banner')) {
		return consentBannerUserPreferencesResourceNew;
	}
	return consentBannerUserPreferencesResourceOld;
};

export const useConsentBannerUserPreferencesResource = () =>
	useResource(getConsentBannerUserPreferencesResource());
